/**
 * useEvents Hook
 *
 * Fetches list of events with optional filters using URQL.
 */

import { useQuery } from "urql";
import type { Event } from "@/generated/graphql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const EventsDocument = `
  query Events(
    $categoryIds: [ID!]
    $timezone: String
    $fromDate: String
    $toDate: String
    $onlyWithMyRsvp: Boolean
    $homeGroupId: ID
  ) {
    events(
      categoryIds: $categoryIds
      timezone: $timezone
      fromDate: $fromDate
      toDate: $toDate
      onlyWithMyRsvp: $onlyWithMyRsvp
      homeGroupId: $homeGroupId
    ) {
      id
      status
      isActive
      title
      subtitle
      description
      venue
      address
      mapsUrl
      startsAt
      imageUrl
      videoUrl
      streamYoutubeUrl
      streamVkUrl
      telegramChatUrl
      inviteText
      slogan
      program
      requiresRsvpConfirmation
      myRsvp {
        id
        status
      }
      hasMyTickets
      categories {
        id
        name
        code
        color
      }
      ticketTypes {
        id
        name
        description
        price
        totalPrice
        currency
        maxQuantity
        soldCount
        available
      }
    }
  }
`;

// =============================================================================
// HOOK
// =============================================================================

interface UseEventsOptions {
  /** Category IDs to filter by */
  categoryIds?: string[];
  /** Timezone for date filtering */
  timezone?: string;
  /** From date for filtering */
  fromDate?: string;
  /** To date for filtering */
  toDate?: string;
  /** Only show events with user's RSVP */
  onlyWithMyRsvp?: boolean;
  /** Home group ID for filtering */
  homeGroupId?: string;
  /** Whether to pause the query */
  pause?: boolean;
}

interface UseEventsResult {
  events: Event[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch list of events with optional filters.
 *
 * Uses URQL for normalized caching and automatic refetch on cache invalidation.
 */
export function useEvents(options: UseEventsOptions = {}): UseEventsResult {
  const {
    categoryIds,
    timezone,
    fromDate,
    toDate,
    onlyWithMyRsvp,
    homeGroupId,
    pause = false,
  } = options;

  const [result, reexecuteQuery] = useQuery<{ events: Event[] }>({
    query: EventsDocument,
    variables: {
      categoryIds,
      timezone,
      fromDate,
      toDate,
      onlyWithMyRsvp,
      homeGroupId,
    },
    pause,
  });

  return {
    events: result.data?.events ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
